import {
  Document,
  Page,
  View,
  Text,
  Link,
  Svg,
  Path,
  StyleSheet,
} from "@react-pdf/renderer";

/* ------------------------------------------------------------------ */
/* Data (same shape as the ThriveFreeStatement React component)        */
/* ------------------------------------------------------------------ */
export const defaultData = {
  rep: "Jordan Michaels",
  planLabel: "Thrive Free Membership",
  memberSince: "June 2022",
  points: {
    balance: { points: "4,850", value: "$485" },
    redeemed: { points: "2,100", value: "$210" },
  },
  tier: {
    current: "Bronze",
    purchases: 12650,
    max: 40000,
    levels: [
      { name: "Bronze", at: 0 },
      { name: "Silver", at: 15000 },
      { name: "Gold", at: 40000 },
    ],
  },
  benefits: [
    {
      label: "Your Points Earnings",
      rows: [
        { text: "1× Points from Henry Schein Products", value: "1.0×" },
        { text: "1× Points from Equipment", value: "1.0×" },
        { text: "1× Points Supplier Bonus", value: "1.0×" },
        { text: "1× Points on All Other Purchases", value: "1.0×" },
      ],
    },
    { label: "Your Shipping Savings", rows: [{ text: "On Merchandise", value: "Standard" }] },
    {
      label: "Your Service Savings",
      locked: true,
      rows: [
        { text: "44% Service Labor Discount" },
        { text: "1 Sterilizer Rental Included" },
        { text: "Technical Advisor Line" },
        { text: "1 Preventative Maintenance Visit" },
      ],
    },
    {
      label: "Your Analytics Savings",
      locked: true,
      rows: [{ text: "Henry Schein Practice Analysis" }, { text: "Henry Schein Fee Analysis" }],
    },
    {
      label: "Supplies Savings",
      sub: "Last 12 Months",
      rows: [{ text: "Savings on Dental Merchandise Purchases", value: "$0" }],
    },
  ],
  comparison: {
    rows: [
      { category: "Points Earnings Value", free: "$126", signature: "$612" },
      { category: "Shipping Savings", free: "$0", signature: "$340" },
      { category: "Service Labor Discount", free: "$0", signature: "$1,180" },
      { category: "Supplies Savings", free: "$0", signature: "$890" },
      { category: "Analytics & Advisory", free: "Not Included", signature: "$220", muted: true },
    ],
    total: { free: "$126", signature: "$3,242" },
    missing: "$3,116",
  },
  path: {
    membership: "$2,499",
    pointsValue: "$485",
    pointsCount: "4,850",
    remaining: "$2,014",
    extraPoints: "20,140",
    ctaLabel: "Ask About Upgrading to Signature",
    ctaUrl: "", // optional: makes the button a clickable link in the PDF
  },
  footnote:
    "*Sample figures shown for mockup purposes only and do not reflect actual member data. Signature savings are estimated from the past 12 months of purchase history at Signature program rates.",
};

const money = (n) => `$${Number(n).toLocaleString("en-US")}`;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */
const NAVY = "#17355a";
const TEAL = "#0f7168";
const AMBER = "#c4721f";
const AMBER_DK = "#a85f17";
const TINT = "#eef3fa";
const CREAM = "#f6efdc";
const CREAM2 = "#fdeedd";
const LINE = "#ececE6";
const MUTED = "#8a8f96";

const s = StyleSheet.create({
  page: {
    backgroundColor: "#fafaf8",
    paddingTop: 32,
    paddingHorizontal: 34,
    paddingBottom: 44,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#1d2733",
  },

  /* header */
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 12,
    marginBottom: 14,
    borderBottomWidth: 2,
    borderBottomColor: NAVY,
  },
  h1: { fontFamily: "Helvetica-Bold", fontSize: 26, color: NAVY },
  pill: {
    alignSelf: "flex-start",
    marginTop: 9,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 10,
    backgroundColor: AMBER,
  },
  pillText: { fontFamily: "Helvetica-Bold", fontSize: 6.5, color: "#fff", letterSpacing: 0.6, textTransform: "uppercase" },
  repLabel: { fontSize: 7.5, color: MUTED, textAlign: "right" },
  repName: { fontFamily: "Helvetica-Bold", fontSize: 9.5, color: NAVY, textAlign: "right" },

  /* card */
  card: {
    marginBottom: 12,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#e3e3dd",
    borderRadius: 7,
    overflow: "hidden",
  },
  cardTitle: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 11,
  },
  cardTitleText: { fontFamily: "Helvetica-Bold", fontSize: 10, color: "#fff" },
  badge: { paddingVertical: 2.5, paddingHorizontal: 8, borderRadius: 8, backgroundColor: "rgba(255,255,255,0.2)" },
  badgeText: { fontFamily: "Helvetica-Bold", fontSize: 6.5, color: "#fff", letterSpacing: 0.6, textTransform: "uppercase" },

  subhead: { flexDirection: "row", backgroundColor: NAVY, paddingVertical: 7, paddingHorizontal: 11, alignItems: "center" },
  subText: { fontFamily: "Helvetica-Bold", fontSize: 7, color: "#fff", letterSpacing: 0.6, textTransform: "uppercase" },

  row: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: LINE },
  cell: { justifyContent: "center", paddingVertical: 7, paddingHorizontal: 11 },
  tint: { backgroundColor: TINT, alignItems: "center" },
  small: { fontSize: 6.5, color: MUTED, marginTop: 1 },

  /* points */
  ptLabel: { flex: 60 },
  ptCol: { flex: 20 },
  ptNum: { fontFamily: "Helvetica-Bold", fontSize: 10.5, color: NAVY },

  /* tier progress */
  tierBody: { paddingTop: 14, paddingBottom: 12, paddingHorizontal: 16 },
  tierTop: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" },
  tierTopText: { fontSize: 8.5, color: "#555" },
  bold: { fontFamily: "Helvetica-Bold" },
  navyBold: { fontFamily: "Helvetica-Bold", color: NAVY },
  amberBold: { fontFamily: "Helvetica-Bold", fontSize: 8.5, color: AMBER },
  track: { height: 6, marginTop: 34, marginHorizontal: 8, borderRadius: 3, backgroundColor: "#e8e4dc" },
  fill: { height: 6, borderRadius: 3, backgroundColor: AMBER },
  node: { position: "absolute", top: -4.5, width: 15, height: 15, marginLeft: -7.5, borderRadius: 7.5, borderWidth: 2 },
  youWrap: { position: "absolute", top: -29, width: 80 },
  youBox: { paddingVertical: 3, paddingHorizontal: 7, borderRadius: 3, backgroundColor: NAVY },
  youText: { fontFamily: "Helvetica-Bold", fontSize: 7, color: "#fff" },
  levels: { position: "relative", height: 26, marginTop: 12, marginHorizontal: 8 },
  level: { position: "absolute", top: 0, width: 60 },
  levelName: { fontFamily: "Helvetica-Bold", fontSize: 8, color: NAVY },
  levelAt: { fontSize: 7.5, color: MUTED },

  /* benefits */
  group: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: LINE },
  groupLabel: { flex: 26, justifyContent: "center", paddingHorizontal: 11, paddingVertical: 7, backgroundColor: "#f6f6f4" },
  groupLabelLocked: { backgroundColor: "#f1f1ec" },
  groupLabelText: { fontFamily: "Helvetica-Bold", fontSize: 8.5, color: NAVY },
  groupRows: { flex: 74 },
  innerRow: { flexDirection: "row", minHeight: 24 },
  innerBorder: { borderTopWidth: 1, borderTopColor: LINE },
  descCell: { flex: 55, justifyContent: "center", paddingHorizontal: 11, paddingVertical: 5 },
  descText: { fontSize: 9, color: "#333" },
  valCell: { flex: 19, justifyContent: "center", alignItems: "center", backgroundColor: TINT },
  valText: { fontFamily: "Helvetica-Bold", fontSize: 10, color: NAVY },
  sigPill: { paddingVertical: 2.5, paddingHorizontal: 6, borderRadius: 8, backgroundColor: CREAM },
  sigPillText: { fontFamily: "Helvetica-Bold", fontSize: 6, color: AMBER_DK },

  /* comparison */
  cmpCat: { flex: 52 },
  cmpFree: { flex: 24, alignItems: "flex-end" },
  cmpSig: { flex: 24, alignItems: "center" },
  cmpText: { fontSize: 9, color: "#333" },
  cmpSigCell: { backgroundColor: CREAM, justifyContent: "center", alignItems: "center" },
  cmpSigText: { fontFamily: "Helvetica-Bold", fontSize: 10, color: AMBER },
  totalRow: { flexDirection: "row", borderTopWidth: 2, borderTopColor: NAVY },
  totalText: { fontFamily: "Helvetica-Bold", fontSize: 9.5, color: NAVY },
  missing: { paddingVertical: 9, paddingHorizontal: 11, backgroundColor: CREAM2 },
  missingText: { fontFamily: "Helvetica-Bold", fontSize: 9, color: AMBER_DK, textAlign: "center" },

  /* path */
  pathBody: { padding: 14 },
  stats: { flexDirection: "row" },
  stat: { flex: 1, paddingVertical: 11, paddingHorizontal: 6, borderRadius: 6, backgroundColor: "#f4f4f2", alignItems: "center" },
  statNum: { fontFamily: "Helvetica-Bold", fontSize: 16, color: NAVY },
  statLabel: { fontSize: 7.5, color: MUTED, marginTop: 3, textAlign: "center" },
  note: { marginTop: 10, padding: 10, borderRadius: 6, backgroundColor: "#f4f4f2" },
  noteText: { fontSize: 9, lineHeight: 1.5, color: "#4a5260" },
  cta: { alignSelf: "flex-start", marginTop: 12, paddingVertical: 8, paddingHorizontal: 14, borderRadius: 5, backgroundColor: AMBER },
  ctaText: { fontFamily: "Helvetica-Bold", fontSize: 9, color: "#fff", textDecoration: "none" },

  footnote: { marginTop: 4, fontSize: 7, lineHeight: 1.5, color: MUTED },
  pageNum: { position: "absolute", bottom: 18, right: 34, fontSize: 7, color: MUTED },
});

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */
const Card = ({ color, title, badge, children, ...rest }) => (
  <View style={s.card} wrap={false} {...rest}>
    <View style={[s.cardTitle, { backgroundColor: color }]}>
      <Text style={s.cardTitleText}>{title}</Text>
      {badge && (
        <View style={s.badge}>
          <Text style={s.badgeText}>{badge}</Text>
        </View>
      )}
    </View>
    {children}
  </View>
);

function PointsSummary({ memberSince, points }) {
  const rows = [
    { label: "Current Points Balance*", ...points.balance },
    { label: "Redeemed Points*", small: "Last 12 Months", ...points.redeemed },
  ];
  return (
    <Card color={TEAL} title="Thrive Rewards Points Summary">
      <View style={s.subhead}>
        <Text style={[s.subText, s.ptLabel]}>Member Since: {memberSince}</Text>
        <Text style={[s.subText, s.ptCol, { textAlign: "center" }]}>Points</Text>
        <Text style={[s.subText, s.ptCol, { textAlign: "center" }]}>Value</Text>
      </View>
      {rows.map((r, i) => (
        <View key={r.label} style={[s.row, i === rows.length - 1 && { borderBottomWidth: 0 }]}>
          <View style={[s.cell, s.ptLabel]}>
            <Text style={{ fontSize: 9, color: "#333" }}>{r.label}</Text>
            {r.small && <Text style={s.small}>{r.small}</Text>}
          </View>
          <View style={[s.cell, s.tint, s.ptCol]}><Text style={s.ptNum}>{r.points}</Text></View>
          <View style={[s.cell, s.tint, s.ptCol]}><Text style={s.ptNum}>{r.value}</Text></View>
        </View>
      ))}
    </Card>
  );
}

function TierProgress({ tier }) {
  const pct = (v) => Math.max(0, Math.min(100, (v / tier.max) * 100));
  const next = tier.levels.find((l) => l.at > tier.purchases);
  const you = pct(tier.purchases);

  // keep the tooltip inside the card near either end of the track
  const tip =
    you < 8 ? { marginLeft: -10, alignItems: "flex-start" }
    : you > 92 ? { marginLeft: -70, alignItems: "flex-end" }
    : { marginLeft: -40, alignItems: "center" };

  return (
    <Card color={AMBER} title="Your Tier Progress">
      <View style={s.tierBody}>
        <View style={s.tierTop}>
          <Text style={s.tierTopText}>
            Current tier: <Text style={s.navyBold}>{tier.current}</Text> · Purchases to date:{" "}
            <Text style={s.navyBold}>{money(tier.purchases)}</Text>
          </Text>
          <Text style={s.amberBold}>
            {next ? `${money(next.at - tier.purchases)} in purchases to ${next.name} Tier` : "Top tier reached"}
          </Text>
        </View>

        {/* track */}
        <View style={s.track}>
          <View style={[s.fill, { width: `${you}%` }]} />

          <View style={[s.youWrap, { left: `${you}%` }, tip]}>
            <View style={s.youBox}><Text style={s.youText}>You: {money(tier.purchases)}</Text></View>
            <Svg width={8} height={4} viewBox="0 0 8 4" style={{ marginHorizontal: 6 }}>
              <Path d="M0 0H8L4 4Z" fill={NAVY} />
            </Svg>
          </View>

          {tier.levels.map((l) => {
            const on = l.at <= tier.purchases;
            return (
              <View
                key={l.name}
                style={[
                  s.node,
                  { left: `${pct(l.at)}%` },
                  on
                    ? { backgroundColor: NAVY, borderColor: NAVY }
                    : { backgroundColor: "#fff", borderColor: "#9aa0a6" },
                ]}
              />
            );
          })}
        </View>

        {/* labels, positioned from l.at */}
        <View style={s.levels}>
          {tier.levels.map((l) => {
            const p = pct(l.at);
            const pos =
              p === 0 ? { marginLeft: -7.5, alignItems: "flex-start" }
              : p === 100 ? { marginLeft: -52.5, alignItems: "flex-end" }
              : { marginLeft: -30, alignItems: "center" };
            return (
              <View key={l.name} style={[s.level, { left: `${p}%` }, pos]}>
                <Text style={s.levelName}>{l.name}</Text>
                <Text style={s.levelAt}>{money(l.at)}</Text>
              </View>
            );
          })}
        </View>
      </View>
    </Card>
  );
}

function Benefits({ tierName, benefits }) {
  return (
    <Card color={NAVY} title={`Your ${tierName} Tier Benefits`} badge="Current Tier">
      {benefits.map((g, gi) => (
        <View
          key={g.label}
          style={[s.group, gi === benefits.length - 1 && { borderBottomWidth: 0 }]}
          wrap={false}
        >
          <View style={[s.groupLabel, g.locked && s.groupLabelLocked]}>
            <Text style={[s.groupLabelText, g.locked && { color: "#9a9a94" }]}>{g.label}</Text>
            {g.sub && <Text style={s.small}>{g.sub}</Text>}
          </View>

          <View style={s.groupRows}>
            {g.rows.map((r, i) => (
              <View key={i} style={[s.innerRow, i > 0 && s.innerBorder]}>
                <View style={s.descCell}>
                  <Text style={[s.descText, g.locked && { color: "#a3a39d" }]}>{r.text}</Text>
                </View>
                <View style={s.valCell}>
                  {g.locked ? (
                    <View style={s.sigPill}><Text style={s.sigPillText}>Signature Only</Text></View>
                  ) : (
                    <Text style={s.valText}>{r.value}</Text>
                  )}
                </View>
              </View>
            ))}
          </View>
        </View>
      ))}
    </Card>
  );
}

function Comparison({ comparison }) {
  return (
    <Card color={AMBER} title="What You Could Be Saving on Thrive Signature">
      <View style={s.subhead}>
        <Text style={[s.subText, s.cmpCat]}>Category</Text>
        <Text style={[s.subText, s.cmpFree, { textAlign: "right" }]}>Thrive Free</Text>
        <Text style={[s.subText, s.cmpSig, { textAlign: "center" }]}>Signature</Text>
      </View>

      {comparison.rows.map((r) => (
        <View key={r.category} style={s.row}>
          <View style={[s.cell, s.cmpCat]}><Text style={s.cmpText}>{r.category}</Text></View>
          <View style={[s.cell, s.cmpFree]}>
            <Text style={[s.cmpText, r.muted && { color: MUTED }]}>{r.free}</Text>
          </View>
          <View style={[s.cell, s.cmpSig, s.cmpSigCell]}><Text style={s.cmpSigText}>{r.signature}</Text></View>
        </View>
      ))}

      <View style={s.totalRow}>
        <View style={[s.cell, s.cmpCat]}><Text style={s.totalText}>Estimated Annual Total</Text></View>
        <View style={[s.cell, s.cmpFree]}><Text style={s.totalText}>{comparison.total.free}</Text></View>
        <View style={[s.cell, s.cmpSig, s.cmpSigCell]}>
          <Text style={[s.totalText, { color: AMBER_DK }]}>{comparison.total.signature}</Text>
        </View>
      </View>

      <View style={s.missing}>
        <Text style={s.missingText}>
          You’re missing an estimated {comparison.missing} per year by staying on Thrive Free
        </Text>
      </View>
    </Card>
  );
}

function UpgradePath({ path }) {
  const button = (
    <View style={s.cta}>
      <Text style={s.ctaText}>{path.ctaLabel}</Text>
    </View>
  );

  return (
    <Card color={AMBER} title="Your Path to Thrive Signature">
      <View style={s.pathBody}>
        <View style={s.stats}>
          <View style={[s.stat, { marginRight: 10 }]}>
            <Text style={s.statNum}>{path.membership}</Text>
            <Text style={s.statLabel}>Signature Annual Membership</Text>
          </View>
          <View style={[s.stat, { marginRight: 10 }]}>
            <Text style={s.statNum}>{path.pointsValue}</Text>
            <Text style={s.statLabel}>Value of Your {path.pointsCount} Points</Text>
          </View>
          <View style={[s.stat, { backgroundColor: CREAM2 }]}>
            <Text style={[s.statNum, { color: AMBER }]}>{path.remaining}</Text>
            <Text style={s.statLabel}>Remaining to Unlock</Text>
          </View>
        </View>

        <View style={s.note}>
          <Text style={s.noteText}>
            Redeem your{" "}
            <Text style={s.navyBold}>{path.pointsCount} points ({path.pointsValue} credit)</Text> and add{" "}
            <Text style={s.navyBold}>{path.remaining} in additional purchases</Text> — about {path.extraPoints}{" "}
            points at your current Bronze earn rate — to cover this year’s Thrive Signature membership in full.
          </Text>
        </View>

        {path.ctaUrl ? <Link src={path.ctaUrl} style={{ textDecoration: "none" }}>{button}</Link> : button}
      </View>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Document                                                            */
/* ------------------------------------------------------------------ */
export default function ThriveFreeStatementPDF({ data = {} }) {
  const d = { ...defaultData, ...data };

  return (
    <Document title="Thrive Free Benefit Statement" author="Henry Schein" subject={d.planLabel}>
      <Page size="LETTER" style={s.page}>
        <View style={s.header}>
          <View>
            <Text style={s.h1}>Benefit Statement</Text>
            <View style={s.pill}><Text style={s.pillText}>{d.planLabel}</Text></View>
          </View>
          <View>
            <Text style={s.repLabel}>Telesales Representative:</Text>
            <Text style={s.repName}>{d.rep}</Text>
          </View>
        </View>

        <PointsSummary memberSince={d.memberSince} points={d.points} />
        <TierProgress tier={d.tier} />
        <Benefits tierName={d.tier.current} benefits={d.benefits} />
        <Comparison comparison={d.comparison} />
        <UpgradePath path={d.path} />

        <Text style={s.footnote}>{d.footnote}</Text>

        <Text
          style={s.pageNum}
          fixed
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
      </Page>
    </Document>
  );
}
